import React, { useState } from "react";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import Box from "@material-ui/core/Box";

import { useGlobal } from "GlobalContext";
import { useHistory } from "react-router-dom";

// @material-ui/icons
import Group from "@material-ui/icons/Group";
import Email from "@material-ui/icons/Email";
import PolicyIcon from "@material-ui/icons/Policy";
import SearchIcon from "@material-ui/icons/Search";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Input from "components/CustomInput/Input.js";
import InfoArea from "components/InfoArea/InfoArea.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";

// social network icons
import x from "../../assets/img/x.png";
import youtube from "../../assets/img/youtube.png";
import facebook from "../../assets/img/facebook.png";
import linkedin from "../../assets/img/linkedin.png";
import instagram from "../../assets/img/instagram.png";

import styles from "./Styles/LoginPageStyles";
import { useTranslation } from "react-i18next";
import { resolveAuthError } from "../../utils/authErrorI18n";

const API_URL = process.env.REACT_APP_API;
const useStyles = makeStyles(styles);

export default function LoginPage() {
  const classes = useStyles();
  const history = useHistory();

  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [reqError, setReqError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { updateSharedValues } = useGlobal();
  const { t } = useTranslation("loginPage");

  /**
   * Traduce un error de login a partir del errorCode devuelto por backend.
   * Si no hay código conocido, usa mensaje fallback traducible.
   */
  const translateLoginError = (errorCode, fallbackMessage) =>
    resolveAuthError(t, {
      page: "login",
      errorCode,
      fallbackMessage,
    });

  /**
   * Obtiene los datos completos del usuario una vez autenticado.
   */
  const fetchUserData = async (username) => {
    const resp = await fetch(`/api/usuarios`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filtro: { username },
      }),
    });

    if (!resp.ok) {
      throw new Error(`HTTP ${resp.status}`);
    }

    const data = await resp.json();

    if (!data.datos || data.datos.length === 0) {
      throw new Error(t("login-error-user-not-found"));
    }

    return data.datos[0];
  };

  /**
   * Envía login y carga contexto de usuario.
   */
  const logIn = async () => {
    if (isSubmitting) return;

    setReqError(null);
    setIsSubmitting(true);

    try {
      const requestBody = {
        emailOrUsername: emailOrUsername.trim(),
        password: password.trim(),
        captchaToken: "captcha-de-prueba",
      };

      const logInResp = await fetch(`/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      let loginData = null;

      try {
        loginData = await logInResp.json();
      } catch (parseError) {
        loginData = null;
      }

      if (!logInResp.ok || loginData?.ok !== true) {
        setReqError(
          translateLoginError(
            loginData?.errorCode,
            loginData?.mensaje || t("login-error-generic")
          )
        );
        return;
      }

      const username = loginData.datos.username;
      const user = await fetchUserData(username);

      updateSharedValues({
        username: user.username,
        nombre: user.nombre,
        email: user.email,
        rol: user.rol,
        telefono: user.telefono,
        biografia: user.biografia,
        fotoPerfil: user.fotoPerfil,
        id: user._id,
        institution: user.institution,
      });

      history.push("/search");
    } catch (err) {
      /**
       * Si el error viene de fetchUserData con un mensaje local,
       * lo respetamos. Si no, usamos el helper de auth.
       */
      const fallbackMessage =
        err?.message === t("login-error-user-not-found")
          ? err.message
          : err?.message || t("login-error-generic");

      setReqError(translateLoginError(err?.errorCode, fallbackMessage));
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Permite enviar login pulsando Enter en cualquier input.
   */
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      logIn();
    }
  };

  return (
    <div className={classes.container}>
      <GridContainer justify="center">
        <GridItem xs={12} sm={12} md={10}>
          <Card className={classes.cardSignup}>
            <h2 className={classes.cardTitle}>{t("login-title")}</h2>

            <CardBody>
              <GridContainer justify="center" spacing={3}>
                {/* Columna informativa */}
                <GridItem xs={12} sm={12} md={5}>
                  <InfoArea
                    title={t("login-info-who-title")}
                    description={t("login-info-who-description")}
                    icon={Group}
                    iconColor="primary"
                  />

                  <InfoArea
                    title={t("login-info-privacy-title")}
                    description={t("login-info-privacy-description")}
                    icon={PolicyIcon}
                    iconColor="primary"
                  />

                  <InfoArea
                    title={t("login-info-goal-title")}
                    description={
                      <>
                        <p style={{ marginTop: 0, marginBottom: 8 }}>
                          {t("login-info-goal-description")}
                        </p>
                        <Link
                          href="https://github.com/uned-cibergid-projects/safemountain"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t("login-info-goal-repo-link")}
                        </Link>
                      </>
                    }
                    icon={SearchIcon}
                    iconColor="primary"
                  />
                </GridItem>

                {/* Columna formulario */}
                <GridItem xs={12} sm={10} md={5}>
                  <form
                    className={classes.form}
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <Input
                      formControlProps={{
                        fullWidth: true,
                        className: classes.customFormControlClasses,
                      }}
                      inputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            className={classes.inputAdornment}
                          >
                            <Email className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                        placeholder: t(
                          "login-input-email-or-username-placeholder"
                        ),
                        value: emailOrUsername,
                        onChange: (e) => setEmailOrUsername(e.target.value),
                        onKeyDown: handleKeyDown,
                      }}
                    />

                    <Input
                      passwordToggle
                      formControlProps={{
                        fullWidth: true,
                        className: classes.customFormControlClasses,
                      }}
                      inputProps={{
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            className={classes.inputAdornment}
                          >
                            <Icon className={classes.inputAdornmentIcon}>
                              lock_outline
                            </Icon>
                          </InputAdornment>
                        ),
                        placeholder: t("login-input-password-placeholder"),
                        type: "password",
                        value: password,
                        onChange: (e) => setPassword(e.target.value),
                        onKeyDown: handleKeyDown,
                      }}
                    />

                    <div className={classes.center}>
                      <Button
                        simple
                        color="primary"
                        onClick={() => history.push("/resetpassword")}
                      >
                        {t("login-forgot-password")}
                      </Button>
                    </div>

                    <Box mt={1} className={classes.center}>
                      <Typography
                        variant="body2"
                        style={{
                          color: "red",
                          minHeight: 24,
                          wordBreak: "break-word",
                        }}
                      >
                        {reqError || ""}
                      </Typography>
                    </Box>

                    <Box mt={1.5} className={classes.center}>
                      <Button
                        round
                        color="primary"
                        onClick={logIn}
                        disabled={isSubmitting}
                      >
                        {t("login-action-submit")}
                      </Button>
                    </Box>

                    <Box mt={4} className={classes.center}>
                      <Typography className={classes.createAccText}>
                        {t("login-no-account")}
                      </Typography>

                      <Box mt={1}>
                        <Button
                          round
                          color="info"
                          onClick={() => history.push("/register")}
                        >
                          {t("login-action-create-account")}
                        </Button>
                      </Box>
                    </Box>
                  </form>
                </GridItem>
              </GridContainer>

              <div className={classes.socialContainer}>
                <a
                  href="https://x.com/AppsDatos"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={x}
                    alt={t("login-social-alt-x")}
                    className={classes.socialIcon}
                  />
                </a>

                <a
                  href="https://www.facebook.com/people/An%C3%A1lisis-de-Apps-y-Protecci%C3%B3n-de-Datos/61572392708560/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={facebook}
                    alt={t("login-social-alt-facebook")}
                    className={classes.socialIcon}
                  />
                </a>

                <a
                  href="https://www.instagram.com/apps.data/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={instagram}
                    alt={t("login-social-alt-instagram")}
                    className={classes.socialIcon}
                  />
                </a>

                <a
                  href="https://www.linkedin.com/company/análisis-de-aplicaciones-móviles-y-protección-de-datos"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={linkedin}
                    alt={t("login-social-alt-linkedin")}
                    className={classes.socialIcon}
                  />
                </a>

                <a
                  href="https://www.youtube.com/@AppsProtData"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={youtube}
                    alt={t("login-social-alt-youtube")}
                    className={classes.socialIcon}
                  />
                </a>
              </div>
              <p className={classes.copyrightContainer}>
                © 2026 Copyright: UNED – INCIBE
              </p>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}